import { useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { ONE_SECOND } from '../../../../../pure-js/libs/Consts'
import { withTimeout } from '../../../../../pure-js/libs/Common'
import useAppState from '../../../hooks/useAppState'
import { upcomingActivitiesListForGamerQuery } from '../../../apiLayer/queryHooks/queries'
import { GamerRoutes } from '../../../config/routes/routeEnums'
import { FinishedGame, isAIDebriefDueAfterGame } from '../helpers/activityHelpers'

/** The longest a finished game waits to learn what comes next before it takes the usual way out. */
const NEXT_ACTIVITY_WAIT_MS = 5 * ONE_SECOND // 5 s

/** The AI debrief page when the team's debrief is due right after this game; nothing otherwise. */
export const useAIDebriefRouteAfterGame = ({ sprintId, activityId, teamId }: Partial<FinishedGame>) => {
  const queryClient = useQueryClient()
  const { state } = useAppState()

  return useCallback(async (): Promise<string | undefined> => {
    if (!sprintId || !activityId || !teamId) return undefined

    try {
      const response = await withTimeout(
        queryClient.fetchQuery(upcomingActivitiesListForGamerQuery(state)),
        NEXT_ACTIVITY_WAIT_MS,
        'next-activity-after-game'
      )
      const activities = response.data ?? []
      const isDebriefDue = isAIDebriefDueAfterGame(activities, { sprintId, activityId, teamId }, Date.now())
      return isDebriefDue ? GamerRoutes.AI_COACH : undefined
    } catch {
      // Not knowing what comes next is no reason to hold the player: they leave the usual way.
      return undefined
    }
  }, [queryClient, state, sprintId, activityId, teamId])
}
